/** Dubai city center — fallback only when office LAT/LNG is not configured. */
export const DUBAI_CENTER = {
  lat: 25.2048493,
  lng: 55.2707828,
  label: 'Dubai Center (fallback)',
};

/** Assumed average driving speed for ETA (km/h). */
export const AVG_SPEED_KMH = 35;

export const parseCoord = (value) => {
  if (value == null || value === '') return null;
  const n = Number(String(value).trim());
  return Number.isFinite(n) ? n : null;
};

/** Haversine distance in kilometers. */
export const haversineKm = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const formatDistanceKm = (km) => {
  if (!Number.isFinite(km)) return '—';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(2)} km`;
};

export const formatDurationMin = (minutes) => {
  if (!Number.isFinite(minutes)) return '—';
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m ? `${h} h ${m} min` : `${h} h`;
};

/** Resolve office start from company profile LAT/LNG; null if not set. */
export const resolveOfficeStart = (profile) => {
  const lat = parseCoord(profile?.LAT);
  const lng = parseCoord(profile?.LNG);
  if (lat == null || lng == null || (lat === 0 && lng === 0)) return null;
  return { lat, lng, label: 'Office' };
};

/**
 * Recalculate leg distances/times for an ordered list of stops (with lat/lng).
 */
export function recalculateRouteLegs(orderedStops, start, speedKmh = AVG_SPEED_KMH) {
  let cumulativeKm = 0;
  let cumulativeMin = 0;
  let current = { lat: start.lat, lng: start.lng };
  return (orderedStops || []).map((stop, index) => {
    const lat = parseCoord(stop.lat);
    const lng = parseCoord(stop.lng);
    let distanceFromPrevKm = 0;
    let durationFromPrevMin = 0;
    if (lat != null && lng != null) {
      distanceFromPrevKm = haversineKm(current.lat, current.lng, lat, lng);
      durationFromPrevMin = (distanceFromPrevKm / speedKmh) * 60;
      cumulativeKm += distanceFromPrevKm;
      cumulativeMin += durationFromPrevMin;
      current = { lat, lng };
    }
    return {
      ...stop,
      lat: lat ?? stop.lat,
      lng: lng ?? stop.lng,
      stopNo: index + 1,
      distanceFromPrevKm,
      durationFromPrevMin,
      cumulativeKm,
      cumulativeMin,
    };
  });
}

/**
 * Nearest-neighbor route from start through stops that have coordinates.
 */
export function optimizeNearestNeighbor(stops, start = DUBAI_CENTER, speedKmh = AVG_SPEED_KMH) {
  const withCoords = [];
  const withoutCoords = [];

  (stops || []).forEach((s) => {
    const lat = parseCoord(s.lat ?? s.LAT ?? s.DeliveryLAT);
    const lng = parseCoord(s.lng ?? s.LNG ?? s.DeliveryLNG);
    if (lat != null && lng != null && !(lat === 0 && lng === 0)) {
      withCoords.push({ ...s, lat, lng });
    } else {
      withoutCoords.push(s);
    }
  });

  const remaining = [...withCoords];
  const ordered = [];
  let current = { lat: start.lat, lng: start.lng };

  while (remaining.length > 0) {
    let bestIdx = 0;
    let bestKm = Infinity;
    for (let i = 0; i < remaining.length; i += 1) {
      const km = haversineKm(current.lat, current.lng, remaining[i].lat, remaining[i].lng);
      if (km < bestKm) {
        bestKm = km;
        bestIdx = i;
      }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    ordered.push(next);
    current = { lat: next.lat, lng: next.lng };
  }

  const route = recalculateRouteLegs(ordered, start, speedKmh);
  const last = route[route.length - 1];

  return {
    start,
    route,
    withoutCoords,
    totalDistanceKm: last?.cumulativeKm || 0,
    totalDurationMin: last?.cumulativeMin || 0,
    speedKmh,
  };
}

/** Build API payload from current route result. */
export function buildRouteDetailPayload(result) {
  if (!result) return [];
  const details = result.route.map((stop) => ({
    Id: stop.id,
    OrderId: stop.orderId,
    LAT: stop.lat != null ? String(stop.lat) : '',
    LNG: stop.lng != null ? String(stop.lng) : '',
    LocationOrder: stop.stopNo,
    TimeFromPrevoous: Number((stop.durationFromPrevMin || 0).toFixed(2)),
    DistanceFromPrevious: Number((stop.distanceFromPrevKm || 0).toFixed(3)),
    CumilativeDistance: Number((stop.cumulativeKm || 0).toFixed(3)),
  }));

  let nextOrder = details.length;
  (result.withoutCoords || []).forEach((o) => {
    nextOrder += 1;
    const prevCum = details.length ? Number(details[details.length - 1].CumilativeDistance) || 0 : 0;
    details.push({
      Id: o.id,
      OrderId: o.orderId,
      LAT: '',
      LNG: '',
      LocationOrder: nextOrder,
      TimeFromPrevoous: 0,
      DistanceFromPrevious: 0,
      CumilativeDistance: prevCum,
    });
  });

  return details;
}
